// Cache is promise based because other script loaders might be running concurrently
// and if they happen to require the same script url, they all must wait until the first
// instance is loaded.
const scriptCache: { [url: string]: PromiseLike<Event> } = {};

function fragmentFromString(doc: Document, strHTML: string): DocumentFragment {
  // @ts-ignore: TS7015
  if (doc.defaultView && doc.defaultView['_phantom'] == null) {
    // Requires IE 11 or greater and not phantom
    return doc.createRange().createContextualFragment(strHTML);
  } else {
    // fallback path. Used by JSDOM and maybe some really old browsers.
    const fragment = doc.createDocumentFragment();
    const temp = doc.createElement('div');
    temp.innerHTML = strHTML;
    for (let child = temp.firstElementChild; child != null; child = temp.firstElementChild) {
      fragment.appendChild(child);
    }
    return fragment;
  }
}

// need to test if a script url is actually going to make an http call. Some script elements
// inline the script as base64 encoded application uri
function isRemote(url: string) {
  return /^((http[s]?|file):)?\/\//.test(url);
}

// Based on: https://stackoverflow.com/a/14174028/897985
function injectScript(parent: HTMLElement, scriptTag: HTMLElement, callback: (ev: Event) => void = () => null) {
  const doc = parent.ownerDocument;
  const script: any = doc.createElement('script');
  const url = scriptTag.getAttribute('src');
  const isRemoteUrl = isRemote(url || '');

  if (url != null) {
    if (scriptCache[url] != null) {
      scriptCache[url].then(callback);
      return;
    }

    script.setAttribute('src', url);
    script.setAttribute('type', 'text/javascript');

    scriptCache[url] = new Promise<Event>((resolve, reject) => {
      if (isRemoteUrl) {
        if (script.addEventListener) {
          //real browsers
          script.addEventListener('load', resolve, false);
        } else {
          //Internet explorer
          script['onreadystatechange'] = function (ev: any) {
            if (/complete|loaded/.test(script['readyState'])) {
              resolve(ev);
            }
          };
        }
      } else {
        // this happens if the script is actually encoded into the URL.
        // Will still deduplicate and immediately callback here
        setTimeout(resolve);
      }
    });

    parent.appendChild(script);
    scriptCache[url].then(callback);
  } else {
    // innerHTML doesn't convert linebreaks to <br/>
    script.innerHTML = scriptTag.textContent as string;

    for (let ii = 0; ii < scriptTag.attributes.length; ii++) {
      const attr = scriptTag.attributes.item(ii);
      script.setAttribute(attr?.name || '', attr?.value || '');
    }

    parent.appendChild(script);
    setTimeout(callback);
  }
}

/**
 * Extracts all script elements from the given html and returns a fragment and a load function, which begins
 * the script loading process when called. Skips urls that have already been loaded.
 *
 * @param html html that contains all scripts and other content
 * @param parent html node to append all script nodes to
 */
export function extractScripts(
  html: string,
  parent: HTMLElement
): { fragment: DocumentFragment; load: () => PromiseLike<void> } {
  const { fragment, scripts } = splitScriptsFromLayout(parent, html);
  return {
    fragment,
    load: () => createScriptLoader(scripts, parent)(),
  };
}

/**
 * Converts the html to a documentFragment based on the parent,
 * then strips out all script nodes.
 *
 * @param parent html node to append the html fragment to
 * @param html html that contains all scripts and other content
 * @return fragment, scripts
 * fragmet -- the document fragment without and scripts
 * scripts -- the list of <script/> nodes removed from the fragment
 */
export function splitScriptsFromLayout(parent: HTMLElement, html: string) {
  const fragment = fragmentFromString(parent.ownerDocument, html);

  // Only extract and load scripts that would be interpreted by the browser as JS
  const scripts = fragment.querySelectorAll<HTMLElement>('script:not([type]),script[type="text/javascript"]');
  for (let ii = 0; ii < scripts.length; ii++) {
    const script = scripts.item(ii);
    script?.parentNode?.removeChild(script);
  }
  return { fragment, scripts };
}

/**
 * Create a promise for loading script nodes on a element
 *
 * @param scripts list of <script/> nodes to load
 * @param parent html element to load the scripts onto
 * @return a Promise for when the script nodes finish loading
 */
export function createScriptLoader(scripts: NodeListOf<HTMLElement>, parent: HTMLElement) {
  let timesExecuted = 0;
  return () =>
    new Promise<void>((resolve, reject) => {
      timesExecuted++;
      if (timesExecuted > 1) {
        // If we've already run this once, running again will likely have unintended consequence
        reject(`Attempted to run script loader ${timesExecuted} times`);
      }
      let loadNext = (ev: Event | null) => {
        resolve();
      };
      for (let ii = scripts.length - 1; ii >= 0; ii--) {
        // build callback chain to mimic synchronous script loading
        const scriptNode = scripts.item(ii);
        const cb = loadNext;
        loadNext = () => injectScript(parent, scriptNode, cb);
      }
      loadNext(null);
    });
}
